import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';
import Text, { TEXT_SIZE, TEXT_COLOR } from '@components/Text';
import CTAButton from '@components/CTAButton';
import EmojiPickerButton from '@components/EmojiPickerButton';
import AudioPlayer from '@components/AudioPlayer';
import { getIconsByType, iconTypes } from '@services/icons';
import { StyledPersonIcon } from '@containers/App/containers/Messaging/components/MessagesList/styles';
import { getMessagingDate, getMessagingTime } from '@utils/timeHelpers';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { formatAudioDuration } from '../../utils/audioDuration';
import { editMessageAction, deleteMessageAction } from '../../actions';
import {
  Wrapper,
  MessageWrapper,
  StyledAvatar,
  Message,
  NewMessagesIndicator,
  SeenIndicator,
  StyledCheckmarkIcon,
  TimeDisplay,
  DateSeparator,
  MessageActions,
  ActionButton,
  EditInput,
  EditActionsRow,
  EditButtonsContainer,
  EditEmojiPickerWrapper,
  AudioMessageWrapper,
  AudioMessageMeta,
  AudioMessageCaption,
  AudioMessageDuration,
} from './styles';

const getPostKind = (post) => post?.payload?.kind || (post?.payload?.attachments?.length ? 'voice' : 'text');

const getTextPayload = (post) => post?.payload?.text || '';

const getFirstAudioAttachment = (post) => {
  const attachments = Array.isArray(post?.payload?.attachments) ? post.payload.attachments : [];
  return attachments.find((attachment) => attachment?.type === 'audio');
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  try {
    return getMessagingTime(timestamp);
  } catch (error) {
    return '';
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  try {
    // Check if date is today
    const date = new Date(parseInt(timestamp, 10));
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (
      date.getFullYear() === today.getFullYear()
      && date.getMonth() === today.getMonth()
      && date.getDate() === today.getDate()
    ) {
      return 'Today';
    }
    if (
      date.getFullYear() === yesterday.getFullYear()
      && date.getMonth() === yesterday.getMonth()
      && date.getDate() === yesterday.getDate()
    ) {
      return 'Yesterday';
    }
    return getMessagingDate(timestamp);
  } catch (error) {
    return '';
  }
};

const parsePosts = (posts, users) => {
  const selfUser = _.find(users, { self: true });
  const otherUsers = _.compact(
    users.map((user) => {
      if (user.self) return null;
      return user.id;
    }),
  );
  const parsedPosts = posts.map((post) => {
    // A post is unread if:
    // 1. It's not from the current user AND
    // 2. The current user's ID is not in the seenBy array
    const unread = !post.self && !post.seenBy?.includes(selfUser.id);
    const seen = selfUser.isMentor && _.difference(otherUsers, post.seenBy).length === 0;
    return {
      ...post,
      unread,
      seen,
    };
  });
  return parsedPosts;
};

// Split the long regex line to avoid linter errors
const linkifyText = (text) => {
  const emailRegex = /(\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b)/gi;
    const urlRegex = /((?:https?:\/\/|ftp:\/\/|www\.|http:\/\/localhost|https:\/\/localhost)[\w\-\.]+(?::\d+)?(?:\/\S*)?)/gi;
  const linkRegex = new RegExp(`${urlRegex.source}|${emailRegex.source}`, 'gi');
  const parts = [];
  let lastIndex = 0;
  let match = linkRegex.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const [fullMatch] = match;
    let linkText = fullMatch;
    let trailingPunctuation = '';
    const punctuationMatch = fullMatch.match(/[.,?!:;]+$/);
    const [punctuation = ''] = punctuationMatch || [];
    if (punctuation) {
      trailingPunctuation = punctuation;
      linkText = fullMatch.slice(0, fullMatch.length - punctuation.length);
    }
    let href = linkText;
    if (linkText.includes('@')) {
      href = `mailto:${linkText}`;
    } else if (!/^(https?:\/\/|ftp:\/\/)/i.test(linkText)) {
      href = `http://${linkText}`;
    }
    const isMeetingLink = /\/video[-]?chat\//i.test(href) || /\/meeting\//i.test(href);
    if (isMeetingLink) {
      parts.push(
        <a
          href={href}
          key={lastIndex}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => {
            e.preventDefault();
            window.open(href, '_blank');
          }}
        >
          {linkText}
        </a>
      );
    } else {
      parts.push(
        <a href={href} key={lastIndex} target="_blank" rel="noopener noreferrer">
          {linkText}
        </a>
      );
    }
    if (trailingPunctuation) {
      parts.push(trailingPunctuation);
    }
    lastIndex = linkRegex.lastIndex;
    match = linkRegex.exec(text);
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
};

const EditIcon = getIconsByType(iconTypes.EDIT);
const DeleteIcon = getIconsByType(iconTypes.DELETE);

class ConversationArea extends Component {
  static isSameDay(timestamp1, timestamp2) {
    if (!timestamp1 || !timestamp2) return false;
    try {
      const date1 = new Date(parseInt(timestamp1, 10));
      const date2 = new Date(parseInt(timestamp2, 10));

      return (
        date1.getFullYear() === date2.getFullYear()
        && date1.getMonth() === date2.getMonth()
        && date1.getDate() === date2.getDate()
      );
    } catch (error) {
      return false;
    }
  }

  static shouldShowDateSeparator = (post, index, posts) => {
    if (!post || !post.createdAt) return false;

    // Always show date separator for the first post
    if (index === posts.length - 1) return true;

    // Check if this post is from a different day than the previous post
    const prevPost = posts[index + 1];
    if (!prevPost || !prevPost.createdAt) return true;

    // Only show date separator if the date is different from the previous post
    return !ConversationArea.isSameDay(post.createdAt, prevPost.createdAt);
  };

  constructor(props) {
    super(props);
    this.state = {
      lastUnreadPostId: null,
      lastSentMessageTimestamp: null,
      editingPostId: null,
      editingText: '',
    };
    this.editInputRef = React.createRef();
    this.editCaretOffset = null;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // Reset lastUnreadPostId if conversation changed
    if (nextProps.currentConversation?.id !== prevState.lastConversationId) {
      return {
        lastUnreadPostId: null,
        lastConversationId: nextProps.currentConversation?.id,
        lastSentMessageTimestamp: null,
      };
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    const { lastSentMessage, currentConversation } = this.props;
    const {
      lastSentMessageTimestamp,
      lastUnreadPostId,
      editingPostId,
      editingText,
    } = this.state;

    // Only update contentEditable if we're starting to edit (not during typing)
    if (editingPostId && editingPostId !== prevState.editingPostId && this.editInputRef.current) {
      const editDiv = this.editInputRef.current;
      if (editDiv.textContent !== editingText) {
        editDiv.textContent = editingText;
      }
    }

    // Clear the indicator every time a message is sent
    if (lastSentMessage && lastSentMessage !== prevProps.lastSentMessage) {
      this.setState({
        lastSentMessageTimestamp: Date.now(),
        lastUnreadPostId: null,
      });
      return;
    }

    if (_.isEmpty(currentConversation)
      || !currentConversation.Thread
      || !Array.isArray(currentConversation.Thread.Posts)
      || !Array.isArray(currentConversation.Users)) {
      return;
    }

    const posts = parsePosts(
      currentConversation.Thread.Posts,
      currentConversation.Users,
    );

    // Find the last unread message
    const lastUnreadPost = posts.findLast((post) => {
      // If we haven't sent a message yet, show all unread messages
      if (!lastSentMessageTimestamp) {
        return post.unread;
      }
      // After sending a message, only show unread messages that came after our last sent message
      return post.unread
        && parseInt(post.createdAt, 10) > lastSentMessageTimestamp;
    });

    const nextLastUnreadPostId = lastUnreadPost?.id || null;

    // Keep this update idempotent to avoid render loops when unread ids are unstable.
    if (nextLastUnreadPostId !== lastUnreadPostId) {
      this.setState({ lastUnreadPostId: nextLastUnreadPostId });
    }
  }

  handleEditClick = (post) => {
    this.setState({
      editingPostId: post.id,
      editingText: getTextPayload(post),
    }, () => {
      // Use requestAnimationFrame to ensure the div is rendered and focused
      requestAnimationFrame(() => {
        if (this.editInputRef.current) {
          const editDiv = this.editInputRef.current;
          editDiv.focus();
          // Set cursor to the end of the text
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(editDiv);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
          this.editCaretOffset = getTextPayload(post).length;
        }
      });
    });
  };

  handleDeleteClick = (postId) => {
    const { openDeleteConfirmPopup, t } = this.props;
    openDeleteConfirmPopup({
      message: t('Actions.Confirmations.confirmDeleteMessage'),
      hideCloseIcon: true,
      onConfirm: () => {
        const { deleteMessage } = this.props;
        deleteMessage(postId);
      },
    });
  };

  handleEditSubmit = (postId) => {
    const { editMessage } = this.props;
    const { editingText } = this.state;
    if (editingText.trim()) {
      editMessage(postId, { text: editingText.trim() });
      this.setState({
        editingPostId: null,
        editingText: '',
      });
    }
  };

  handleEditCancel = () => {
    this.setState({
      editingPostId: null,
      editingText: '',
    });
  };

  handleEditChange = (e) => {
    const newText = e.target.textContent || e.target.innerText || '';
    this.setState({ editingText: newText });
    this.captureEditCaretOffset();
  };

  getCaretOffset = (container, selectionRange) => {
    const preCaretRange = selectionRange.cloneRange();
    preCaretRange.selectNodeContents(container);
    preCaretRange.setEnd(selectionRange.endContainer, selectionRange.endOffset);
    return preCaretRange.toString().length;
  };

  setCaretOffset = (container, targetOffset) => {
    const selection = window.getSelection();
    const range = document.createRange();
    const safeOffset = Math.max(0, targetOffset);
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    let currentNode = walker.nextNode();
    let traversed = 0;

    while (currentNode) {
      const nextTraversed = traversed + currentNode.textContent.length;
      if (safeOffset <= nextTraversed) {
        range.setStart(currentNode, safeOffset - traversed);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }

      traversed = nextTraversed;
      currentNode = walker.nextNode();
    }

    range.selectNodeContents(container);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  captureEditCaretOffset = () => {
    if (!this.editInputRef.current) {
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (this.editInputRef.current.contains(range.startContainer)) {
      this.editCaretOffset = this.getCaretOffset(this.editInputRef.current, range);
    }
  };

  handleEditEmojiSelect = (emoji) => {
    if (!this.editInputRef.current) {
      return;
    }

    const { editingText } = this.state;
    const fallbackOffset = editingText.length;
    const start = Number.isFinite(this.editCaretOffset) ? this.editCaretOffset : fallbackOffset;
    const safeStart = Math.max(0, Math.min(start, editingText.length));
    const nextText = `${editingText.slice(0, safeStart)}${emoji}${editingText.slice(safeStart)}`;
    const nextOffset = safeStart + emoji.length;

    this.setState({ editingText: nextText }, () => {
      if (this.editInputRef.current) {
        this.editInputRef.current.textContent = nextText;
        this.editInputRef.current.focus();
        this.setCaretOffset(this.editInputRef.current, nextOffset);
        this.editCaretOffset = nextOffset;
      }
    });
  };

  handleEditKeyDown = (e) => {
    // Allow Enter key to create new lines, but prevent form submission
    if (e.key === 'Enter' && !e.shiftKey) {
      // Don't prevent default - let Enter create a new line
      return;
    }
    // Handle Escape key to cancel editing
    if (e.key === 'Escape') {
      this.handleEditCancel();
    }
  };

  render() {
    const { currentConversation, t } = this.props;
    const { editingPostId, editingText, lastUnreadPostId } = this.state;
    if (_.isEmpty(currentConversation)) return null;

    const posts = parsePosts(
      currentConversation.Thread.Posts,
      currentConversation.Users,
    );
    const lastSeenIndex = posts.findIndex((post) => post.seen);

    return (
      <Wrapper>
        {posts.map((post, index) => {
          const isFirstUnread = post.id === lastUnreadPostId;
          const isQueue = index !== posts.length - 1
            && post.self === posts[index + 1].self
            && !isFirstUnread;
          const isLastSeen = index === lastSeenIndex;
          const showDateSeparator = ConversationArea.shouldShowDateSeparator(post, index, posts);
          const isEditing = post.id === editingPostId;
          const postKind = getPostKind(post);
          const audioAttachment = getFirstAudioAttachment(post);
          const isTextPost = postKind === 'text';
          const isVoicePost = !isTextPost && Boolean(audioAttachment);

          return (
            <div key={post.id}>
              {showDateSeparator && (
                <DateSeparator>
                  <Text
                    size={TEXT_SIZE.T4}
                    textColor={TEXT_COLOR.DISABLED}
                  >
                    {formatDate(post.createdAt)}
                  </Text>
                </DateSeparator>
              )}

              {isFirstUnread && (
                <NewMessagesIndicator>
                  <Text
                    size={TEXT_SIZE.T3}
                    textColor={TEXT_COLOR.DISABLED}
                    margin="0 24px"
                  >
                    {t('Informative.Notifying.newMessages')}
                  </Text>
                </NewMessagesIndicator>
              )}

              <MessageWrapper self={post.self} isQueue={isQueue}>
                {!isQueue && (
                  post.publisherUser?.avatar
                    ? <StyledAvatar src={post.publisherUser.avatar} />
                    : <StyledPersonIcon />
                )}
                <Message self={post.self} isQueue={isQueue}>
                  {isEditing ? (
                    <EditInput
                      contentEditable
                      suppressContentEditableWarning
                      onInput={this.handleEditChange}
                      onKeyUp={this.captureEditCaretOffset}
                      onMouseUp={this.captureEditCaretOffset}
                      onFocus={this.captureEditCaretOffset}
                      onKeyDown={this.handleEditKeyDown}
                      ref={this.editInputRef}
                    />
                  ) : isVoicePost ? (
                    <AudioMessageWrapper>
                      {audioAttachment?.url ? (
                        <AudioPlayer
                          src={audioAttachment.url}
                          sourceKey={audioAttachment.mediaId || post.id}
                        />
                      ) : (
                        <Text size={TEXT_SIZE.T5} textColor={TEXT_COLOR.DISABLED}>
                          {t('Informative.Negative.voiceMessageNotAvailable')}
                        </Text>
                      )}
                      <AudioMessageMeta>
                        <AudioMessageCaption>
                          <Text size={TEXT_SIZE.T5} textColor={TEXT_COLOR.DISABLED}>
                            {t('Informative.Notifying.voiceMessage')}
                          </Text>
                        </AudioMessageCaption>
                        <AudioMessageDuration>
                          <Text size={TEXT_SIZE.T5} textColor={TEXT_COLOR.DISABLED}>
                            {formatAudioDuration(audioAttachment.durationSeconds)}
                          </Text>
                        </AudioMessageDuration>
                      </AudioMessageMeta>
                    </AudioMessageWrapper>
                  ) : (
                    <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
                      {linkifyText(getTextPayload(post))}
                    </Text>
                  )}
                  {isEditing && (
                    <EditActionsRow>
                      <EditEmojiPickerWrapper>
                        <EmojiPickerButton
                          onSelect={this.handleEditEmojiSelect}
                          recentStorageKey='novotalk_recent_emojis_messages_edit'
                        />
                      </EditEmojiPickerWrapper>
                      <EditButtonsContainer>
                        <CTAButton
                          secondary
                          onClick={this.handleEditCancel}
                        >
                          {t('Actions.Instance.cancel')}
                        </CTAButton>
                        <CTAButton
                          variant="primary"
                          onClick={() => this.handleEditSubmit(post.id)}
                          disabled={!editingText.trim()}
                        >
                          {t('Actions.Instance.save')}
                        </CTAButton>
                      </EditButtonsContainer>
                    </EditActionsRow>
                  )}
                  <TimeDisplay self={post.self}>
                    <Text
                      size={TEXT_SIZE.T5}
                      textColor={TEXT_COLOR.DISABLED_LIGHT}
                    >
                      {formatTime(post.createdAt)}
                    </Text>
                    {post.self && !isEditing && isTextPost && (
                      <MessageActions className="message-actions">
                        <ActionButton
                          onClick={() => this.handleEditClick(post)}
                          title={t('Actions.Instance.edit')}
                        >
                          <EditIcon />
                        </ActionButton>
                        <ActionButton
                          onClick={() => this.handleDeleteClick(post.id)}
                          title={t('Actions.Instance.delete')}
                        >
                          <DeleteIcon />
                        </ActionButton>
                      </MessageActions>
                    )}
                    {post.self && !isEditing && !isTextPost && (
                      <MessageActions className="message-actions">
                        <ActionButton
                          onClick={() => this.handleDeleteClick(post.id)}
                          title={t('Actions.Instance.delete')}
                        >
                          <DeleteIcon />
                        </ActionButton>
                      </MessageActions>
                    )}
                  </TimeDisplay>
                </Message>
              </MessageWrapper>

              {isLastSeen && (
                <SeenIndicator>
                  <StyledCheckmarkIcon />
                  <Text
                    size={TEXT_SIZE.T5}
                    textColor={TEXT_COLOR.DISABLED_LIGHT}
                  >
                    {t('Common.Statuses.seen')}
                  </Text>
                </SeenIndicator>
              )}
            </div>
          );
        })}
      </Wrapper>
    );
  }
}

ConversationArea.propTypes = {
  currentConversation: PropTypes.object,
  t: PropTypes.func,
  editMessage: PropTypes.func.isRequired,
  deleteMessage: PropTypes.func.isRequired,
  openDeleteConfirmPopup: PropTypes.func.isRequired,
  lastSentMessage: PropTypes.string,
};

const mapDispatchToProps = (dispatch) => ({
  editMessage: (postId, payload) => dispatch(editMessageAction(postId, payload)),
  deleteMessage: (postId) => dispatch(deleteMessageAction(postId)),
  openDeleteConfirmPopup: (props) => dispatch(openPopUp(POPUP_TYPES.DELETE_MESSAGE_CONFIRM, props)),
});

export default compose(
  withTranslation(),
  connect(null, mapDispatchToProps),
)(ConversationArea);
